import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, XCircle, AlertTriangle,
  Calendar, Phone, Globe, ShieldCheck,
  User, Award, MapPin, Newspaper
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Custom Verified Stamp Component matching the requested design
const VerifiedStamp = () => (
  <motion.div
    initial={{ scale: 0, rotate: -15 }}
    animate={{ scale: 1, rotate: 0 }}
    transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 20 }}
    className="absolute -bottom-6 -right-6 w-[85px] h-[85px] drop-shadow-xl z-20 pointer-events-none opacity-85"
  >
    <svg viewBox="0 0 200 200" className="w-full h-full">
      <defs>
        {/* Top arc for text (left to right) */}
        <path id="topArc" d="M 35, 100 A 65, 65 0 0, 1 165, 100" />
        {/* Bottom arc for text (right to left) so it reads upright */}
        <path id="bottomArc" d="M 165, 100 A 65, 65 0 0, 1 35, 100" />
      </defs>

      {/* Outer scalloped edge effect */}
      <circle cx="100" cy="100" r="92" fill="#16A34A" />
      <circle cx="100" cy="100" r="92" fill="none" stroke="#16A34A" strokeWidth="12" strokeDasharray="16 10" />

      {/* Inner white rings */}
      <circle cx="100" cy="100" r="92" fill="none" stroke="white" strokeWidth="3" />
      <circle cx="100" cy="100" r="52" fill="none" stroke="white" strokeWidth="3" />

      {/* Circular Text */}
      <text fill="white" fontSize="26" fontWeight="bold" letterSpacing="4">
        <textPath xlinkHref="#topArc" startOffset="50%" textAnchor="middle">
          VERIFIED
        </textPath>
      </text>

      <text fill="white" fontSize="26" fontWeight="bold" letterSpacing="4">
        <textPath xlinkHref="#bottomArc" startOffset="50%" textAnchor="middle">
          VERIFIED
        </textPath>
      </text>

      {/* Main Center Checkmark */}
      <path
        d="M 70,105 L 90,120 L 130,70"
        fill="none"
        stroke="white"
        strokeWidth="14"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </motion.div>
);

export default function Verify() {
  const { token } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['verify', token],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/public/verify/${token}`);
      return data;
    },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7F9]">
        <div className="bg-white rounded-3xl p-10 shadow-xl max-w-sm w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#16A34A] border-t-transparent mx-auto"></div>
          <p className="text-[#666666] mt-6 font-medium">Verifying Credentials...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7F9] p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden"
        >
          <div className="bg-[#DC2626] p-8 text-center">
            <XCircle className="w-16 h-16 text-white mx-auto" />
            <h1 className="text-2xl text-white font-bold mt-4 font-serif">Invalid Status</h1>
          </div>
          <div className="p-8 text-center text-[#4A4A4A]">
            <p>This verification link is invalid or has expired.</p>
            <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
              <p className="text-sm font-bold text-[#1A1A1A]">Fact India News</p>
              <p className="text-xs text-[#666666] mt-1">Verification Department</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  const isVerified = data.verify_status === 'VALID';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6] p-4 sm:p-6 font-sans relative overflow-hidden">
      {/* Dynamic Background Element */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#16A34A]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#D0021B]/5 rounded-full blur-[120px]" />

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] max-w-md w-full overflow-hidden relative border border-white"
      >
        {/* Top Header - Minimalist */}
        <div className="px-8 pt-4 flex items-center justify-between">

          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase ${isVerified ? 'bg-[#16A34A]/10 text-[#16A34A]' : 'bg-[#DC2626]/10 text-[#DC2626]'}`}>
            {isVerified ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {data.verify_status}
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4">
          {/* Main Profile Header */}
          <div className="flex flex-col items-center text-center">
            {/* Photo with STAMP Overlay */}
            <div className="relative mb-6">
              <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white shadow-lg bg-[#F3F4F6]">
                {data.card?.photo_base64 ? (
                  <img
                    src={data.card.photo_base64}
                    alt={data.card.full_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D0021B] text-4xl font-serif">
                    {data.card?.full_name?.charAt(0)}
                  </div>
                )}
              </div>

              {/* VERIFIED STAMP - Custom Designed Overlay */}
              {isVerified && <VerifiedStamp />}
            </div>

            <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] leading-tight">
              {data.card?.full_name}
            </h2>
            <p className="text-[#D0021B] font-bold mt-1 uppercase tracking-widest text-[10px]">
              {data.card?.designation}
            </p>
           
                <p className="text-[16px] font-serif font-semibold text-[#1A1A1A] leading-tight">ID No : {data.card?.id_number}</p>
          </div>

          {/* Details Grid */}
          <div className="mt-4 space-y-4 bg-[#F8FAFC] p-6 rounded-2xl border border-[#EDF2F7]">
         

            {data.card?.mobile && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#E2E8F0]">
                  <Phone className="w-4 h-4 text-[#64748B]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#64748B] uppercase font-bold tracking-widest">Mobile No</p>
                  <p className="text-[#1A1A1A] font-medium text-sm">{data.card.mobile}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#E2E8F0]">
                <Calendar className="w-4 h-4 text-[#64748B]" />
              </div>
              <div>
                <p className="text-[10px] text-[#64748B] uppercase font-bold tracking-widest">Valid Until</p>
                <p className="text-[#1A1A1A] font-medium text-sm">
                  {data.card?.expiry_date ? new Date(data.card.expiry_date).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric'
                  }) : '-'}
                </p>
              </div>
            </div>

            {data.organization && (
              <div className="flex items-center gap-4 border-t border-[#E2E8F0] pt-4 mt-2">
                <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#E2E8F0]">
                  <Newspaper className="w-4 h-4 text-[#64748B]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#64748B] uppercase font-bold tracking-widest">Publication</p>
                  <p className="text-[#1A1A1A] font-medium text-sm">{data.organization.name}</p>
                  {data.organization.rni_number && data.organization.rni_number !== '-' && (
                    <p className="text-[10px] text-[#D0021B] mt-0.5">RNI: {data.organization.rni_number}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Bar */}
        <div className="bg-[#D0021B] px-8 py-4 flex items-center justify-center">
          <p className="text-[9px] text-white uppercase tracking-[0.2em] font-bold text-center">
            Approved By - {data.organization?.approved_by}
            <br />
            {data.organization?.ministry}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
